import { Program, Quest } from '../types';
import { firestoreService } from './firestoreService';

/**
 * Program Recovery Service
 * Handles detection and recovery of interrupted AI program generations
 */
class ProgramRecoveryService {
  private readonly MAX_GENERATION_TIME = 15 * 60 * 1000; // 15 minutes
  private readonly MAX_RETRY_ATTEMPTS = 3;
  
  /**
   * Check all user programs for incomplete generations and attempt recovery
   */
  async checkAndRecoverPrograms(
    userId: string, 
    programs: Program[], 
    quests: Quest[],
    generateProgressiveQuests: (program: Program, phase: number) => Promise<any>
  ): Promise<void> {
    console.log('🔍 Checking for incomplete program generations...');
    
    for (const program of programs) {
      // Skip non-AI programs
      const isAIProgram = (program.aiFollowUpAnswers && Object.keys(program.aiFollowUpAnswers).length > 0) ||
                         (!program.questTemplates || program.questTemplates.length === 0);
      
      if (!isAIProgram) continue;
      
      // Check for pending or stuck generations
      if (program.generationStatus) {
        const needsRecovery = this.needsRecovery(program);
        
        if (needsRecovery) {
          console.log(`🚑 Program "${program.title}" needs recovery for Phase ${program.generationStatus.phase}`);
          await this.recoverProgram(userId, program, quests, generateProgressiveQuests);
        }
      }
      
      // Check for missing first week quests
      const firstWeekQuests = this.getPhaseQuests(program, quests, 1);
      if (firstWeekQuests.length === 0 && program.currentPhase === 1) {
        console.log(`🚑 Program "${program.title}" missing first week quests - attempting recovery`);
        await this.recoverProgram(userId, program, quests, generateProgressiveQuests, 1);
      }
    }
  }
  
  /**
   * Determine if a program needs recovery
   */
  private needsRecovery(program: Program): boolean {
    if (!program.generationStatus) return false;
    
    const { status, startedAt, retryCount = 0 } = program.generationStatus;
    
    // Don't retry if we've exceeded max attempts
    if (retryCount >= this.MAX_RETRY_ATTEMPTS) {
      console.log(`⚠️ Program "${program.title}" has exceeded max retry attempts (${retryCount}/${this.MAX_RETRY_ATTEMPTS})`);
      return false;
    }
    
    // Recover if failed
    if (status === 'failed') {
      return true;
    }
    
    // Recover if pending (never started)
    if (status === 'pending') {
      return true;
    }
    
    // Recover if stuck in progress for too long
    if (status === 'in_progress' && startedAt) {
      const timeSinceStart = Date.now() - startedAt;
      if (timeSinceStart > this.MAX_GENERATION_TIME) {
        console.log(`⏰ Generation for "${program.title}" stuck for ${Math.floor(timeSinceStart / 60000)} minutes`);
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * Attempt to recover a program by regenerating quests
   */
  private async recoverProgram(
    userId: string,
    program: Program,
    quests: Quest[],
    generateProgressiveQuests: (program: Program, phase: number) => Promise<any>,
    forcedPhase?: number
  ): Promise<void> {
    const phaseToRecover = forcedPhase || program.generationStatus?.phase || 1;
    
    try {
      console.log(`🔄 Attempting to recover Phase ${phaseToRecover} for program "${program.title}"`);
      
      // Update status to in_progress
      await this.updateGenerationStatus(userId, program.id, {
        phase: phaseToRecover,
        status: 'in_progress',
        startedAt: Date.now(),
        retryCount: (program.generationStatus?.retryCount || 0) + 1
      });
      
      // Attempt to generate quests
      await generateProgressiveQuests(program, phaseToRecover);
      
      // Update status to completed
      await this.updateGenerationStatus(userId, program.id, {
        phase: phaseToRecover,
        status: 'completed',
        completedAt: Date.now()
      });
      
      // Add phase to phasesGenerated array
      const phasesGenerated = program.phasesGenerated || [];
      if (!phasesGenerated.includes(phaseToRecover)) {
        await firestoreService.updateProgram(program.id, {
          phasesGenerated: [...phasesGenerated, phaseToRecover]
        });
      }
      
      console.log(`✅ Successfully recovered Phase ${phaseToRecover} for program "${program.title}"`);
    } catch (error: any) {
      console.error(`❌ Failed to recover Phase ${phaseToRecover}:`, error);
      
      // Update status to failed
      await this.updateGenerationStatus(userId, program.id, {
        phase: phaseToRecover,
        status: 'failed',
        error: error.message || 'Recovery failed',
        completedAt: Date.now()
      });
    }
  }
  
  /**
   * Update generation status in Firestore
   */
  private async updateGenerationStatus(
    userId: string,
    programId: string,
    statusUpdate: Partial<Program['generationStatus']>
  ): Promise<void> {
    try {
      await firestoreService.updateProgram(programId, {
        generationStatus: statusUpdate as any
      });
    } catch (error) {
      console.error('Failed to update generation status:', error);
    }
  }
  
  /**
   * Get quests for a specific phase
   */
  private getPhaseQuests(program: Program, quests: Quest[], phase: number): Quest[] {
    return quests.filter(quest => {
      if (!quest.programId || quest.programId !== program.id) return false;
      if (!quest.scheduledFor) return false;
      
      const questDate = new Date(quest.scheduledFor);
      const startDate = new Date(program.startDate);
      const dayOffset = Math.floor((questDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      const questPhase = Math.floor(dayOffset / 7) + 1;
      
      return questPhase === phase;
    });
  }
  
  /**
   * Check if a specific phase has already been generated (duplicate check)
   */
  isPhaseGenerated(program: Program, quests: Quest[], phase: number): boolean {
    // Check phasesGenerated array first (most reliable)
    if (program.phasesGenerated && program.phasesGenerated.includes(phase)) {
      return true;
    }
    
    // Fallback: check if quests exist for this phase
    const phaseQuests = this.getPhaseQuests(program, quests, phase);
    return phaseQuests.length > 0;
  }
}

export const programRecoveryService = new ProgramRecoveryService();

